import Component from '@glimmer/component';
import { eq } from 'ember-truth-helpers';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import type { ColumnHeader } from 'garaje/components/ui/table';
import UiTable from 'garaje/components/ui/table';
import UiCursorPagination from 'garaje/components/ui-pagination/cursor';
import type VisitComposite from 'garaje/models/visit-composite';

import DatetimeCell from './datetime-cell';
import RecurringRuleCell from './recurring-rule-cell';
import SignInTimeCell from './sign-in-time-cell';
import SignOutTimeCell from './sign-out-time-cell';
import StatusCell from './status-cell';

interface VisitsTableSignature {
  Args: {
    visits: VisitComposite[];
    recordCount: number;
    pageSize: number;
    previousCursor: string;
    nextCursor: string;
    setCursor: (cursor: string | null) => void;
    tableType?: 'upcoming' | 'previous';
    isLoading?: boolean;
  };
  Element: HTMLDivElement;
}

const COLUMN_HEADERS: ColumnHeader[] = [
  {
    key: 'recurringRule',
    label: '',
    sortable: false,
    class: 'w-[1%]',
    componentOptions: {
      // @ts-expect-error: TS does not like matching class components to `ComponentLike`
      template: RecurringRuleCell,
    },
  },
  {
    key: 'visitTime',
    label: 'Date and time',
    sortable: false,
    class: 'w-[15%]',
    componentOptions: {
      // @ts-expect-error: TS does not like matching class components to `ComponentLike`
      template: DatetimeCell,
    },
  },
  {
    key: 'locationName',
    label: 'Location',
    sortable: false,
    class: 'w-[10%]',
  },
  {
    key: 'flowName',
    label: 'Purpose',
    sortable: false,
    class: 'w-[10%]',
  },
  {
    key: 'host',
    label: 'Host',
    sortable: false,
    class: 'w-[10%]',
  },
  {
    key: 'status',
    label: 'Status',
    sortable: false,
    class: 'w-[10%]',
    componentOptions: {
      // @ts-expect-error: TS does not like matching class components to `ComponentLike`
      template: StatusCell,
    },
  },
  {
    key: 'signInTime',
    label: 'Signed in',
    sortable: false,
    class: 'w-[8%]',
    componentOptions: {
      template: SignInTimeCell,
    },
  },
  {
    key: 'signOutTime',
    label: 'Signed out',
    sortable: false,
    class: 'w-[8%]',
    componentOptions: {
      template: SignOutTimeCell,
    },
  },
];

export default class VisitsTable extends Component<VisitsTableSignature> {
  get visibleColumns(): ColumnHeader[] {
    const tableType = this.args.tableType;
    return COLUMN_HEADERS.filter((column) => {
      if (tableType === 'upcoming') {
        return !['signInTime', 'signOutTime'].includes(column.key);
      }
      return true;
    });
  }

  get emptyStateMessage(): string {
    return this.args.tableType === 'upcoming' ? 'No upcoming visits' : 'No previous visits';
  }

  <template>
    <div ...attributes class="relative" data-test-visits-table-container>
      {{#if @isLoading}}
        <div class="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
          <LoadingSpinnerPulse />
        </div>
      {{/if}}

      {{#if (eq @visits.length 0)}}
        <span class="text-carbon-50 font-normal block">
          {{this.emptyStateMessage}}
        </span>
      {{else}}
        <UiTable
          class="mb-4 !mt-0"
          data-test-visits-table
          @columnHeaders={{this.visibleColumns}}
          @data={{@visits}}
          @headerClass="text-sm pt-1 pb-3 px-3 align-middle font-medium"
        />
        <UiCursorPagination @nextCursor={{@nextCursor}} @previousCursor={{@previousCursor}} @setCursor={{@setCursor}} />
      {{/if}}
    </div>
  </template>
}
